import React, { useEffect, useState } from "react";
import { useOutletContext, useParams } from "react-router-dom";
import { Button, Col, Label, Row, Table } from "reactstrap";
import { toast } from "react-toastify";
import { useDispatch } from "react-redux";
import { Form, Formik } from "formik";
import { t } from "i18next";
import * as Yup from "yup";

import { CompanyId } from "../../constants";
import LocalstorageService from "../../helpers/localstorage-service";
import { AmountSchema } from "../../helpers/yup-schema";
import { numberFormatter } from "../../helpers";
import BankService from "../../services/bank";
import BranchService from "../../services/branch";
import AgentService from "../../services/agent";
import profitabilityService from "../../services/profitability";

import { setLoadingOverlay } from "../../store/slices/loadingOverlaySlice";

import SelectField from "../../components/SelectField";
import SortableHeader from "../../components/SortableHeader";
import NoRecords from "../../components/Table/NoRecords";
// import ActionBtns from "../../components/ActionBtns";
import NumericFormatField from "../../components/NumericFormatField";

const HeaderList = [
  { title: t("BANK.TITLE"),                  style: { minWidth: "120px" } },
  { title: t("BRANCH.TITLE"),                style: { minWidth: "120px" } },
  { title: t("EMPLOYEE.ACC_NO_LABEL"),       style: { minWidth: "100px" } },
  { title: t("EMPLOYEE.MAX_WITHDRAWAL_LABEL"), style: { minWidth: "100px", textAlign: "right" } },
  { title: "עמלת מזומן %",                   style: { minWidth: "100px", textAlign: "center" } },
  { title: "הנחה %",                         style: { minWidth: "80px",  textAlign: "center" } },
  { title: "שיעור אפקטיבי",                  style: { minWidth: "100px", textAlign: "center" } },
  { title: "",                               style: { minWidth: "60px"  } },
];

const InitialValues = {
  bankId: "",
  bankBranchId: "",
  accountNumber: "",
  amount: "",
};

const ValidationSchema = AmountSchema().shape({
  bankId: Yup.string().required(t("REQUIRED")),
  bankBranchId: Yup.string().required(t("REQUIRED")),
  accountNumber: Yup.string().required(t("REQUIRED")),
});

let companyId = 0;

function BankAffiliation({ formData }) {
  // eslint-disable-next-line no-unused-vars
  const [setPageTitle, setCrumbItem] = useOutletContext();
  const dispatch = useDispatch();
  const params = useParams();

  const [initApiCount, setInitApiCount] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [bankName, setBankName] = useState("");
  const [branchName, setBranchName] = useState("");
  const [bank, setBank] = useState([]);
  const [bankBranch, setBankBranch] = useState([]);
  const [tableData, setTableData] = useState([]);
  // עריכת עמלות בנק: { rowIdx: { cashFeeRate, cashFeeDiscount } }
  const [feeEdits, setFeeEdits] = useState({});

  useEffect(() => {
    setCrumbItem([
      { title: t("AGENT.TITLE"), link: "list" },
      { title: t("UPDATE_TITLE"), link: "#" },
    ]);

    companyId = LocalstorageService.getItem(CompanyId);

    getBank();
    // getBankBranch(formData.bankId);
    getBankInfoFn();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (initApiCount === 2) {
      dispatch(setLoadingOverlay(false));
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initApiCount]);

  function handleBranchId({ value, label }) {
    setBankName(label);
    setBankBranch([]);
    getBankBranch(value);
  }

  async function getBank() {
    dispatch(setLoadingOverlay(true));
    await BankService.listBanks()
      .then((response) => {
        const { status, dataList } = response.data;

        if (status === 400) {
          return;
        }

        if (dataList && dataList.length) {
          const data = dataList.map((item) => ({
            id: item.id,
            label: item.bankName,
            value: item.id,
          }));

          setBank(data);
        }
      })
      .finally(() => {
        setInitApiCount((prev) => prev + 1);
      });
  }

  async function getBankBranch(bankId) {
    dispatch(setLoadingOverlay(true));
    await BranchService.listBranches({ bankId })
      .then((response) => {
        const { status, dataList } = response.data;

        if (status === 400) {
          return;
        }

        if (dataList && dataList.length) {
          const data = dataList.map((item) => ({
            id: item.id,
            label: item.branchName,
            value: item.id,
          }));

          setBankBranch(data);
        }
      })
      .finally(() => {
        dispatch(setLoadingOverlay(false));
      });
  }

  async function getBankInfoFn() {
    dispatch(setLoadingOverlay(true));
    await AgentService.bankAffiInfo({
      companyId,
      agentId: parseInt(params.agentId),
    })
      .then((response) => {
        const { status, dataList } = response.data;

        if (status === 400) {
          return;
        }

        dataList.forEach((item) => {
          item.formattedAmount = numberFormatter(item.amount);
        });

        setTableData(dataList || []);
        // אתחול feeEdits מהנתונים שנטענו
        const initFees = {};
        (dataList || []).forEach((item, idx) => {
          initFees[idx] = {
            cashFeeRate:     item.cashFeeRate     ?? 0,
            cashFeeDiscount: item.cashFeeDiscount ?? 0,
          };
        });
        setFeeEdits(initFees);
      })
      .finally(() => {
        setInitApiCount((prev) => prev + 1);
      });
  }

  const handleAddEntry = async (values, { resetForm, setSubmitting }) => {
    setSubmitting(false);

    const payload = {
      ...values,
      bankId: parseInt(values.bankId),
      bankBranchId: parseInt(values.bankBranchId),
      bankName,
      branchName,
      amount: parseFloat(values.amount),
      formattedAmount: numberFormatter(values.amount),
    };

    setTableData((pre) => {
      return pre.concat([payload]);
    });

    resetForm();
  };

  const handleSubmit = async () => {
    const payload = {
      agentId: parseInt(params.agentId),
      companyId,
      bankMappings: tableData,
    };

    setIsLoading(true);
    await AgentService.bankAffiliation(payload)
      .then((response) => {
        const { status, message } = response.data;

        if (status === 400) {
          toast.error(message);
          return;
        }

        toast.success(message);
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  const deleteHandler = (index) => {
    const affList = [...tableData];
    affList.splice(index, 1);
    setTableData(affList);
  };

  const handleFeeChange = (idx, field, value) => {
    setFeeEdits(prev => ({
      ...prev,
      [idx]: { ...prev[idx], [field]: value },
    }));
  };

  const saveFee = async (idx) => {
    const item = tableData[idx];
    if (!item?.bankAccountId) return;
    const fee = feeEdits[idx] || {};
    try {
      const res = await profitabilityService.updateBankCashFee({
        companyId,
        bankAccountId:   item.bankAccountId,
        cashFeeRate:     parseFloat(fee.cashFeeRate)     || 0,
        cashFeeDiscount: parseFloat(fee.cashFeeDiscount) || 0,
      });
      if (res.data?.status === 200) toast.success('עמלות עודכנו בהצלחה');
      else toast.error(res.data?.message || 'שגיאה בשמירה');
    } catch {
      toast.error('שגיאה בשמירת עמלות');
    }
  };

  const calcEffective = (idx) => {
    const fee = feeEdits[idx] || {};
    const r = parseFloat(fee.cashFeeRate)     || 0;
    const d = parseFloat(fee.cashFeeDiscount) || 0;
    const eff = (r / 100) * (1 - d / 100);
    return (eff * 100).toFixed(4) + '%';
  };

  return (
    <React.Fragment>
      <Formik
        initialValues={InitialValues}
        enableReinitialize={true}
        validationSchema={ValidationSchema}
        onSubmit={handleAddEntry}
      >
        {({ values, isSubmitting, setFieldValue, setFieldTouched }) => (
          <Form noValidate>
            <Row>
              {/* Bank */}
              <Col xs={6} lg={3}>
                <div className="mb-4">
                  <SelectField
                    fieldName="bankId"
                    fieldLabel={t("BANK.TITLE")}
                    fieldValue={values.bankId}
                    fieldOptions={bank}
                    fieldChange={(option) => {
                      option && handleBranchId(option);

                      setFieldValue("bankBranchId", "");
                      setFieldTouched("bankBranchId", false);
                    }}
                    setFieldValue={setFieldValue}
                    setFieldTouched={setFieldTouched}
                    isRequired={true}
                  />
                </div>
              </Col>

              {/* Branch */}
              <Col xs={6} lg={3}>
                <div className="mb-4">
                  <SelectField
                    fieldName="bankBranchId"
                    fieldLabel={t("BRANCH.TITLE")}
                    fieldValue={values.bankBranchId}
                    fieldOptions={bankBranch}
                    fieldChange={(option) => {
                      option && setBranchName(option.label);
                    }}
                    setFieldValue={setFieldValue}
                    setFieldTouched={setFieldTouched}
                    isRequired={true}
                    isDisabled={!values.bankId}
                  />
                </div>
              </Col>

              {/* Account */}
              <Col sm={6} lg={3}>
                <div className="mb-4">
                  <NumericFormatField
                    fieldName="accountNumber"
                    fieldValue={values.accountNumber}
                    fieldLabel={t("EMPLOYEE.ACC_NO_LABEL")}
                    useThousandSeparator={false}
                    fieldPlaceholder={t("EMPLOYEE.ACC_NO_PLACEHOLDER")}
                    setFieldValue={setFieldValue}
                    setFieldTouched={setFieldTouched}
                  />
                </div>
              </Col>

              {/* Max Withdrawal */}
              <Col xs={10} sm={5} lg={2}>
                <div className="mb-4">
                  <NumericFormatField
                    fieldName="amount"
                    fieldValue={values.amount}
                    fieldLabel={t("EMPLOYEE.MAX_WITHDRAWAL_LABEL")}
                    fieldPlaceholder={t("EMPLOYEE.MAX_WITHDRAWAL_PLACEHOLDER")}
                    setFieldValue={setFieldValue}
                    setFieldTouched={setFieldTouched}
                  />
                </div>
              </Col>

              <Col xs={2} sm={1}>
                <div className="mb-4">
                  <div className="">
                    <Label>&nbsp;</Label>
                  </div>
                  <Button color="success" type="submit">
                    <i className="ri-add-line"></i>
                  </Button>
                </div>
              </Col>
            </Row>

            <Table striped bordered responsive size="sm">
              <thead>
                <tr>
                  <SortableHeader headers={HeaderList} isDisabled={true} />
                </tr>
              </thead>

              <tbody>
                {tableData.map((item, l0Idx) => (
                  <tr key={l0Idx}>
                    <td className="text-center">
                      <Button
                        color="danger"
                        size="sm"
                        title={t("EMPLOYEE.DEL_BANK_AFF")}
                        onClick={() => deleteHandler(l0Idx)}
                      >
                        {t("DELETE_TITLE")}
                      </Button>
                    </td>
                    <td>{item.bankName}</td>
                    <td>{item.branchName}</td>
                    <td>{item.accountNumber}</td>
                    <td align="right">{item.formattedAmount}</td>
                    {/* עמלת מזומן */}
                    <td className="text-center" style={{ minWidth: 90 }}>
                      <input type="number" className="form-control form-control-sm text-center"
                        style={{ width: 80 }}
                        step="0.01" min="0" max="5"
                        value={feeEdits[l0Idx]?.cashFeeRate ?? ''}
                        onChange={e => handleFeeChange(l0Idx, 'cashFeeRate', e.target.value)}
                      />
                    </td>
                    {/* הנחה */}
                    <td className="text-center" style={{ minWidth: 80 }}>
                      <input type="number" className="form-control form-control-sm text-center"
                        style={{ width: 70 }}
                        step="1" min="0" max="100"
                        value={feeEdits[l0Idx]?.cashFeeDiscount ?? ''}
                        onChange={e => handleFeeChange(l0Idx, 'cashFeeDiscount', e.target.value)}
                      />
                    </td>
                    {/* אפקטיבי */}
                    <td className="text-center">
                      <span className="badge bg-info text-dark">{calcEffective(l0Idx)}</span>
                    </td>
                    {/* שמור */}
                    <td className="text-center">
                      {item.bankAccountId && (
                        <Button color="primary" size="sm" onClick={() => saveFee(l0Idx)}>
                          <i className="ri-save-line" />
                        </Button>
                      )}
                    </td>
                  </tr>
                ))}

                {!isLoading && tableData.length === 0 && (
                  <NoRecords colSpan={HeaderList.length + 1} />
                )}
              </tbody>
            </Table>

            <div className="button-items mt-4">
              {/* Submit Button */}
              <Button
                color="primary"
                className="w-md waves-effect waves-light"
                type="button"
                onClick={handleSubmit}
                disabled={isSubmitting || isLoading || tableData.length === 0}
              >
                {isSubmitting || isLoading ? t("PLEASE_WAIT") : t("SUBMIT")}
              </Button>
            </div>
          </Form>
        )}
      </Formik>
    </React.Fragment>
  );
}

export default BankAffiliation;
